import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, ThumbsUp, Minus, ThumbsDown } from 'lucide-react';
import { api } from '../context/AuthContext';

const RATINGS = [
  { value: 'very_useful', label: 'Very Useful', icon: ThumbsUp, color: 'text-green-400 hover:bg-green-500/10 border-green-500/30' },
  { value: 'somewhat_useful', label: 'Somewhat Useful', icon: Minus, color: 'text-amber-400 hover:bg-amber-500/10 border-amber-500/30' },
  { value: 'not_useful', label: 'Not Useful', icon: ThumbsDown, color: 'text-red-400 hover:bg-red-500/10 border-red-500/30' },
];

export function FeedbackModal({ open, onOpenChange, bidId }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => onOpenChange?.(false);

  const handleSubmit = async () => {
    if (!rating) { toast.error('Please select a rating'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('rating', rating);
      fd.append('comment', comment);
      if (bidId) fd.append('bid_id', bidId);
      await api.post('/feedback', fd);
      setSubmitted(true);
      toast.success('Thank you for your feedback');
    } catch {
      toast.error('Failed to submit feedback');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-sm">
          <div className="text-center py-4 space-y-2">
            <MessageSquare className="w-8 h-8 text-green-400 mx-auto" />
            <p className="text-sm text-slate-200">Thanks for your feedback.</p>
            <p className="text-xs text-slate-500">It helps us build a better product.</p>
            <Button variant="ghost" className="text-slate-400 text-xs mt-2" onClick={handleClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-sm" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-sm">How useful was this analysis?</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Your feedback helps us improve BidSentry.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 justify-center py-2">
          {RATINGS.map(r => (
            <button
              key={r.value}
              onClick={() => setRating(r.value)}
              className={`flex flex-col items-center gap-1 px-4 py-3 rounded-sm border transition-all ${rating === r.value ? `${r.color} bg-slate-800` : 'border-slate-800 text-slate-600 hover:border-slate-700'}`}
              data-testid={`feedback-${r.value}`}
            >
              <r.icon className="w-5 h-5" />
              <span className="text-[9px] uppercase tracking-wider">{r.label}</span>
            </button>
          ))}
        </div>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Any additional thoughts? (optional)"
          className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs p-2 rounded-sm resize-none h-16"
          data-testid="feedback-comment"
        />
        <DialogFooter>
          <Button variant="ghost" className="text-slate-500 text-xs" onClick={handleClose}>Skip</Button>
          <Button onClick={handleSubmit} disabled={loading || !rating} className="bg-orange-600 hover:bg-orange-700 text-white text-xs" data-testid="feedback-submit-btn">
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
